import { format, getDay } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";

/**
 * Returns the slots that fall on the same display-timezone calendar day as the
 * clicked cell. Used by Quick Add to decide between an instant default add (no
 * slots yet) and opening the picker to add ANOTHER slot (day already populated).
 *
 * @param {Date} clickedDate - Display-timezone date of the clicked calendar cell
 *   (already zoned via toDisplayDate / toZonedTime, so `format(clickedDate,
 *   "yyyy-MM-dd")` returns the correct local date string)
 * @param {Array<{id: string, start: Date|string, end: Date|string}>} slots - Current slots (UTC)
 * @param {string} effectiveTimezone - Active display timezone (IANA string)
 * @returns {Array} the subset of `slots` on the clicked day, in input order
 */
export function getSlotsOnDay(clickedDate, slots, effectiveTimezone) {
  const clickedDateStr = format(clickedDate, "yyyy-MM-dd");
  return slots.filter((slot) => {
    const utcStart = slot.start instanceof Date ? slot.start : new Date(slot.start);
    const slotDateStr = formatInTimeZone(utcStart, effectiveTimezone, "yyyy-MM-dd");
    return slotDateStr === clickedDateStr;
  });
}

/**
 * Suggests the start time ("HH:mm" in the display timezone) for ADDING ANOTHER
 * slot to a day that already has slots — immediately after the latest existing
 * slot ends, so the proposed slot can't collide with the one Quick Add just
 * created (the user only has to confirm, not re-pick a time).
 *
 * Falls back to `fallbackTime` when (a) the day has no slots yet, or (b) the
 * latest slot ends on a LATER calendar day (e.g. an overnight slot) — stamping
 * that "HH:mm" back onto the clicked day would land in the small hours.
 *
 * @param {Date} clickedDate - Display-timezone date of the clicked calendar cell
 * @param {Array<{start: Date|string, end: Date|string}>} slots - Current slots (UTC)
 * @param {string} effectiveTimezone - Active display timezone (IANA string)
 * @param {string} fallbackTime - "HH:mm" session default for the clicked day
 * @returns {string} "HH:mm"
 */
export function suggestNextSlotTime(clickedDate, slots, effectiveTimezone, fallbackTime) {
  const clickedDateStr = format(clickedDate, "yyyy-MM-dd");
  const onDay = getSlotsOnDay(clickedDate, slots, effectiveTimezone);
  if (onDay.length === 0) return fallbackTime;

  const latestEnd = onDay
    .map((slot) => (slot.end instanceof Date ? slot.end : new Date(slot.end)))
    .reduce((a, b) => (a.getTime() >= b.getTime() ? a : b));

  // Only suggest the gap-free time when the latest slot still ends on the
  // clicked day; an overnight slot would otherwise push the suggestion to the
  // small hours of that same morning.
  if (formatInTimeZone(latestEnd, effectiveTimezone, "yyyy-MM-dd") !== clickedDateStr) {
    return fallbackTime;
  }
  return formatInTimeZone(latestEnd, effectiveTimezone, "HH:mm");
}

/**
 * Decides what a Quick Add day-tap should do, given the day's existing slots.
 * Pure + deterministic — the caller applies the result and enforces the
 * past-time guard (which depends on the wall clock, so it stays out here).
 *
 *  - empty day        → `{ kind: "add", ... }`        (drop a slot instantly)
 *  - day with slot(s) → `{ kind: "openPicker", ... }` (open the picker, pre-filled
 *                        with a collision-free start time, instead of deleting)
 *
 * @param {Date} clickedDate - Display-timezone date of the clicked calendar cell
 * @param {Array<{start: Date|string, end: Date|string}>} slots - Current slots (UTC)
 * @param {string} effectiveTimezone - Active display timezone (IANA string)
 * @param {{time: string, durationMinutes: number}} sessionDefault - Default for the day
 * @returns {{kind: "add"|"openPicker", time: string, durationMinutes: number}}
 */
export function planQuickAddTap(clickedDate, slots, effectiveTimezone, sessionDefault) {
  const { time, durationMinutes } = sessionDefault;
  if (getSlotsOnDay(clickedDate, slots, effectiveTimezone).length === 0) {
    return { kind: "add", time, durationMinutes };
  }
  return {
    kind: "openPicker",
    time: suggestNextSlotTime(clickedDate, slots, effectiveTimezone, time),
    durationMinutes,
  };
}

/**
 * Builds a new slot object for Quick Add mode using session defaults for the given date.
 *
 * @param {Date} clickedDate - Display-timezone date of the clicked calendar cell
 * @param {string} effectiveTimezone - Active display timezone (IANA string)
 * @param {string} time - Default start time string (e.g. "18:00")
 * @param {number} durationMinutes - Duration in minutes
 * @returns {{ id: string, start: Date, end: Date }}
 */
export function buildQuickAddSlot(clickedDate, effectiveTimezone, time, durationMinutes) {
  const dateStr = format(clickedDate, "yyyy-MM-dd");
  const startUtc = fromZonedTime(`${dateStr}T${time}:00`, effectiveTimezone);
  const endUtc = new Date(startUtc.getTime() + Number(durationMinutes || 0) * 60 * 1000);
  return { id: crypto.randomUUID(), start: startUtc, end: endUtc };
}

/**
 * Returns the weekday (0-6, Sunday=0) for a display-timezone date.
 * This is identical to `getDay` from date-fns because `toDisplayDate`/`toZonedTime`
 * shifts the Date object so its components represent the target timezone.
 *
 * @param {Date} displayDate
 * @returns {number}
 */
export function getQuickAddWeekday(displayDate) {
  return getDay(displayDate);
}
